import { has } from 'lodash'
import PropTypes from 'prop-types'
import { ReactNode, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'

import { getCurrentSpaceId } from '@/components/App/selectors'
import { useRoles } from '@/components/RolesContainer'
import messages from '@/messages'
import { useAppSelector } from '@/store/hooks'

import { getUserById, getUserBySpaceAndId } from './basicQueries'

export interface UserData {
  id: string
  displayName: string
  email: string
  name: {
    familyName: string
    givenName: string
  }
  roles: {
    spaces: any
  }
  photos: [
    {
      value: string
      valueUnsigned: string
      display: string
      primary: boolean
    }
  ]
}
export interface UserResponse {
  data: UserData
  status: string
}

export const USER_ATTRIBUTES = ['userName', 'name.formatted', 'name.familyName', 'name.givenName', 'displayName']

const User = ({ userId, shouldRequestUser, ...props }: any) => {
  const intl = useIntl()

  const roles = useRoles()
  const spaceId = useAppSelector(getCurrentSpaceId)

  const isSpaceAdmin = roles.hasSpaceAdminRole(spaceId)

  const basicUserQuery = useQuery<UserResponse>({ ...getUserBySpaceAndId({ userId }), enabled: !isSpaceAdmin })
  const userQuery = useQuery<UserResponse>({ ...getUserById({ userId }), enabled: isSpaceAdmin })

  const userData = isSpaceAdmin ? userQuery?.data : basicUserQuery?.data

  useEffect(() => {
    if (!userData?.data && shouldRequestUser) {
      isSpaceAdmin ? userQuery.refetch() : basicUserQuery.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.data, userId, shouldRequestUser])

  const getUserDisplayName = () => {
    if (userData?.data?.displayName === 'unknown') {
      return intl.formatMessage(messages.unknownUser)
    } else {
      return userData?.data?.displayName
    }
  }

  // Any failed requests to get a user will result in Unkown user being displayed as handled in global user reducer
  let contents: ReactNode =
    getUserDisplayName() ||
    (has(userData, 'data.name.givenName') &&
      has(userData, 'data.name.familyName') &&
      `${userData?.data?.name?.givenName} ${userData?.data?.name?.familyName}`) ||
    intl.formatMessage(messages.unknownUser)

  if (basicUserQuery.isLoading || userQuery.isLoading) {
    contents = <div className="loader__spinner" />
  }

  return (
    <div {...props} className="user">
      {contents}
    </div>
  )
}

User.propTypes = {
  userId: PropTypes.string.isRequired,
  shouldRequestUser: PropTypes.bool
}

User.defaultProps = {
  shouldRequestUser: true
}

export default User
